import {
    type NativeTableID,
    type TableName,
    type TableGlideType,
    type TypeSchema,
    getTableName,
    type SourceMetadata,
    getNativeTableSourceMetadata,
} from "@glide/type-schema";
import { assert } from "@glideapps/ts-necessities";
import type { SQLTableName } from "./firebase-function-types";
import { getSourceMetadataForTable } from "./components/SerializedApp";
import { getFeatureSetting } from "./feature-settings";

// This will return the native table ID for the query, which is what we use as
// the query ID.
export function getQueryIDForTable(
    sourceMetadata: readonly SourceMetadata[] | undefined,
    table: TableGlideType
): NativeTableID | undefined {
    let sm: SourceMetadata | undefined;

    if (getFeatureSetting("preferSourceMetadataInTable")) {
        sm = getSourceMetadataForTable(sourceMetadata, table);
    } else {
        const tableName = getTableName(table);

        if (sourceMetadata !== undefined) {
            sm = getNativeTableSourceMetadata(sourceMetadata, tableName);
        }
        if (sm === undefined) {
            sm = table.sourceMetadata;
        }
    }

    if (sm?.type !== "Native table") return undefined;
    if (sm.needsQuery !== true) return undefined;

    return sm.id;
}

export function findTableNameForQuery(
    sourceMetadata: readonly SourceMetadata[] | undefined,
    schema: TypeSchema | undefined,
    queryID: NativeTableID
): TableName | undefined {
    if (schema !== undefined && getFeatureSetting("preferSourceMetadataInTable")) {
        for (const t of schema.tables) {
            if (t.sourceMetadata?.type !== "Native table") continue;
            if (t.sourceMetadata.id !== queryID) continue;
            assert(t.sourceMetadata.needsQuery === true);
            return getTableName(t);
        }
    }

    function testSourceMetadata(sm: SourceMetadata): TableName | undefined {
        if (sm.type !== "Native table") return undefined;
        if (sm.id !== queryID) return undefined;
        assert(sm.needsQuery === true);
        return sm.tableName;
    }

    if (sourceMetadata !== undefined) {
        for (const sm of sourceMetadata) {
            const tableName = testSourceMetadata(sm);
            if (tableName !== undefined) {
                return tableName;
            }
        }
    }
    if (schema !== undefined) {
        for (const t of schema.tables) {
            if (t.sourceMetadata === undefined) continue;
            const tableName = testSourceMetadata(t.sourceMetadata);
            if (tableName !== undefined) {
                return tableName;
            }
        }
    }
    return undefined;
}

export function getDisplayNameForSQLTableName(name: SQLTableName): string {
    return name.join(".");
}
